// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import * as Adaptive from 'adaptivecards';
import DraggableElement from './DraggableElement';
import { IPoint } from './Miscellaneous';
import DesignerPeer from './DesignerPeers/DesignerPeer';
import CardElementPeerRegistry from './CardElementPeerRegistry';
import ActionPeerRegistry from './ActionPeerRegistry';
import ActionPeer from './DesignerPeers/ActionPeer';
import CardElementPeer from './DesignerPeers/CardElementPeer';
import { generateUUID } from '../utilities/Utils';
import { MaxActions } from './CardConstants';
import {
    ContextMenu,
    ContextualMenuOperations,
} from '../components/CustomFabricUIElements/ContextualMenu';
import { createRoot } from 'react-dom/client';
import React from 'react';

type PeerDetails = {
    peer: DesignerPeer;
    parent: DesignerPeer;
    index: number;
    isNewPeer: boolean;
    grabPosition: IPoint;
};

export default class CardDesignerSurface {
    static readonly cardElementPeerRegistry: CardElementPeerRegistry =
        new CardElementPeerRegistry();
    static readonly actionPeerRegistry: ActionPeerRegistry =
        new ActionPeerRegistry();

    private _updateCount: number = 0;

    private _card: Adaptive.AdaptiveCard;
    private _allPeers: Array<DesignerPeer> = [];
    private _rootPeer: DesignerPeer;
    private _cardHost: HTMLElement;
    private _fold: HTMLElement;
    private _designerSurface: HTMLDivElement;
    private _selectedPeer: DesignerPeer;
    private _onPeerSelected: (sender: DesignerPeer) => void;
    private _draggedPeer: DesignerPeer;
    private _draggedPeerDetails: PeerDetails;
    private _draggedPeerVisual: HTMLElement = null;
    private _currentMousePosition: IPoint;
    private _dropTarget: DesignerPeer;
    private _lastParseErrors: Array<Adaptive.IValidationError> = [];
    private _preventJsonUpdate: boolean = false;
    private _copiedJSON: any;
    // This is to pass the modified peer back to the desinger
    private _peerToInsert: DesignerPeer;
    private pasteDisabled: boolean = true;

    public setSelectedPeer(value: DesignerPeer) {
        if (this._selectedPeer !== value) {
            if (this._selectedPeer) {
                this._selectedPeer.isSelected = false;
            }

            this._selectedPeer = value;

            if (this._selectedPeer) {
                this._selectedPeer.isSelected = true;
            }

            if (this.onSelectedPeerChanged) {
                this.onSelectedPeerChanged(this._selectedPeer);
            }
        }
    }

    private peerChanged(peer: DesignerPeer, updatePropertySheet: boolean) {
        this._lastParseErrors = [];

        this.renderCard();
        this.updateLayout();

        if (updatePropertySheet && this.onSelectedPeerChanged) {
            this.onSelectedPeerChanged(this._selectedPeer);
        }
    }

    private peerRemoved(peer: DesignerPeer) {
        this._allPeers.splice(this._allPeers.indexOf(peer), 1);

        if (peer === this._selectedPeer) {
            this.setSelectedPeer(null);
        }

        if (this._updateCount === 0) {
            this.renderCard();
            this.updateLayout();
        }
    }

    private renderCard() {
        this._cardHost.innerHTML = '';

        if (this.card) {
            if (this.onCardValidated) {
                this.onCardValidated(
                    this._lastParseErrors,
                    this.card.validateProperties(),
                );
            }

            let cardToRender: Adaptive.AdaptiveCard = this.card;
            cardToRender.designMode = true;

            let renderedCard = cardToRender.render();
            let designerHost = this._cardHost.parentElement;
            designerHost.style.overflow = 'hidden';
            designerHost.style.position = 'unset';

            if (this.fixedHeightCard) {
                renderedCard.style.height = '100%';
            }
            this._cardHost.appendChild(renderedCard);
            designerHost.appendChild(this._fold);
            const foldLineHeight = 530;

            if (
                this._cardHost.getBoundingClientRect().height > foldLineHeight
            ) {
                this._fold.style.display = 'flex';
                this._fold.style.width =
                    designerHost.getBoundingClientRect().width + 54 + 'px';
                this._fold.style.top = foldLineHeight + 'px';
            } else {
                this._fold.style.display = 'none';
            }
        }
    }

    private addPeer(peer: DesignerPeer) {
        if (this._allPeers.indexOf(peer) < 0) {
            this._allPeers.push(peer);

            peer.render();

            peer.onSelectedChanged = (peer: DesignerPeer) => {
                if (peer.isSelected) {
                    this.setSelectedPeer(peer);
                    if (typeof this._onPeerSelected === 'function') {
                        this._onPeerSelected(peer);
                    }
                } else {
                    if (this._selectedPeer === peer) {
                        this.setSelectedPeer(null);
                    }
                }
            };
            peer.onChanged = (
                sender: DesignerPeer,
                updatePropertySheet: boolean,
            ) => {
                this.peerChanged(sender, updatePropertySheet);
            };
            peer.onPeerRemoved = (sender: DesignerPeer) => {
                this.peerRemoved(sender);
            };
            peer.onPeerAdded = (
                sender: DesignerPeer,
                newPeer: DesignerPeer,
            ) => {
                this.addPeer(newPeer);
                this.updateLayout();
            };
            peer.onStartDrag = (sender: DraggableElement, e: PointerEvent) => {
                this.startDrag(sender as DesignerPeer, e);
            };
            peer.onEndDrag = (sender: DraggableElement) => {
                this.endDrag();
            };

            peer.addElementsToDesignerSurface(this._designerSurface);

            for (var i = 0; i < peer.getChildCount(); i++) {
                this.addPeer(peer.getChildAt(i));
            }
        }
    }

    private internalFindDropTarget(
        pointerPosition: IPoint,
        currentPeer: DesignerPeer,
        forPeer: DesignerPeer,
    ): DesignerPeer {
        if (currentPeer === forPeer) {
            return null;
        }

        var result: DesignerPeer = null;
        var lookDeeper = currentPeer instanceof ActionPeer;

        if (!lookDeeper) {
            var boundingRect = currentPeer.getBoundingRect();

            lookDeeper = boundingRect.isInside(pointerPosition);
        }

        if (lookDeeper) {
            let canDrop = currentPeer.canDrop(forPeer);

            if (canDrop) {
                result = currentPeer;
            }

            for (var i = 0; i < currentPeer.getChildCount(); i++) {
                var deeperResult = this.internalFindDropTarget(
                    pointerPosition,
                    currentPeer.getChildAt(i),
                    forPeer,
                );

                if (deeperResult) {
                    result = deeperResult;

                    break;
                }
            }
        }

        return result;
    }

    private findCardElementPeer(
        cardElement: Adaptive.CardElement,
    ): CardElementPeer {
        for (var i = 0; i < this._allPeers.length; i++) {
            var peer = this._allPeers[i];

            if (
                peer instanceof CardElementPeer &&
                peer.cardElement === cardElement
            ) {
                return peer;
            }
        }

        return null;
    }

    private findActionPeer(action: Adaptive.Action): ActionPeer {
        for (var i = 0; i < this._allPeers.length; i++) {
            var peer = this._allPeers[i];

            if (peer instanceof ActionPeer && peer.action === action) {
                return peer;
            }
        }

        return null;
    }

    private inlineCardExpanded(
        action: Adaptive.ShowCardAction,
        isExpanded: boolean,
    ) {
        let peer = this.findCardElementPeer(action.card);

        if (isExpanded) {
            if (!peer) {
                let registration =
                    CardDesignerSurface.cardElementPeerRegistry.findTypeRegistration(
                        Adaptive.AdaptiveCard,
                    );

                peer = new registration.peerType(
                    peer,
                    this,
                    registration,
                    action.card,
                );

                let parentPeer = this.findActionPeer(action);

                if (parentPeer) {
                    parentPeer.insertChild(peer);
                } else {
                    this._rootPeer.insertChild(peer);
                }
            } else {
                peer.addElementsToDesignerSurface(this._designerSurface, true);
            }
        } else {
            if (peer) {
                peer.removeElementsFromDesignerSurface(true);
            }
        }

        this.updateLayout();
    }

    private cutSelectedPeer() {
        this._copiedJSON = this._selectedPeer.getCardObject().toJSON();
        this.selectedPeer.remove(false, true);
    }

    private copySelectedPeer() {
        this._copiedJSON = this._selectedPeer.getCardObject().toJSON();
    }

    private pasteBelowSelectedPeer() {
        try {
            if (this._copiedJSON.type.startsWith('Action.')) {
                // for stand alone action types
                this.pasteActionElements(
                    this.selectedPeer.parent,
                    this._copiedJSON,
                );
            } else {
                //for all other card elements
                this.pasteCardElements(
                    this.selectedPeer.parent,
                    this._copiedJSON,
                );
            }
        } catch (err) {
            console.log('Something went wrong', err);
        }
    }

    private handleKeyDown = (e: KeyboardEvent) => {
        let cancelPropagation = false;
        if (this._selectedPeer) {
            switch (e.key) {
                case 'Delete':
                case 'Backspace':
                    this.removeSelected();
                    cancelPropagation = true;
                    break;

                case 'c':
                    if (e.ctrlKey || e.metaKey) {
                        // copy support
                        this.copySelectedPeer();
                        cancelPropagation = true;
                    }
                    break;

                case 'x':
                    if (e.ctrlKey || e.metaKey) {
                        // cut support
                        this.cutSelectedPeer();
                        cancelPropagation = true;
                    }
                    break;

                case 'v':
                    if (e.ctrlKey || e.metaKey) {
                        //paste copied/cut component
                        this.pasteBelowSelectedPeer();
                        cancelPropagation = true;
                    }
                    break;
            }
        }
        if (cancelPropagation) {
            e.stopPropagation();
        }
    };

    private get draggedPeer(): DesignerPeer {
        return this._draggedPeer;
    }

    private set draggedPeer(value: DesignerPeer) {
        if (this._draggedPeer !== value) {
            if (this._draggedPeer) {
                this._draggedPeer.dragging = false;
            }

            this._draggedPeer = value;

            if (this._draggedPeer) {
                this._draggedPeer.dragging = true;
            }
        }
    }

    public showCustomMenu(ev) {
        const onClickCallback = (value: string) => {
            switch (value) {
                case ContextualMenuOperations.Cut: {
                    this.cutSelectedPeer();
                    this.pasteDisabled = false;
                    break;
                }
                case ContextualMenuOperations.Copy: {
                    this.copySelectedPeer();
                    this.pasteDisabled = false;
                    break;
                }
                case ContextualMenuOperations.Paste: {
                    this.pasteBelowSelectedPeer();
                    break;
                }
                case ContextualMenuOperations.Delete: {
                    this.removeSelected();
                    break;
                }
            }
        };

        let container: HTMLElement = document.createElement('div');
        let contextualMenu = React.createElement(ContextMenu, {
            onClickCallback: onClickCallback,
            mouseEvent: ev,
            pasteDisabled: this.pasteDisabled,
        });
        const containerRoot = createRoot(container);
        containerRoot.render(contextualMenu);
    }

    readonly parentElement: HTMLElement;

    constructor(
        parentElement: HTMLElement,
        onPeerSelected: (sender: DesignerPeer) => void,
    ) {
        this.parentElement = parentElement;
        this._onPeerSelected = onPeerSelected;

        this._fold = document.getElementById('fold');
        if (this._fold === null) {
            this._fold = document.createElement('div');
            this._fold.id = 'fold';
            this._fold.classList.add('fold');

            let foldLine: HTMLElement = document.createElement('hr');
            foldLine.classList.add('foldLine');

            let leftFoldText = document.createElement('p');
            leftFoldText.classList.add('foldText');
            leftFoldText.innerText = 'Fold';
            leftFoldText.style.flexGrow = '0';

            let rightFoldText = document.createElement('p');
            rightFoldText.classList.add('foldText');
            rightFoldText.innerText = 'Fold';

            this._fold.appendChild(leftFoldText);
            this._fold.appendChild(foldLine);
            this._fold.appendChild(rightFoldText);
        }
        var rootElement = document.createElement('div');
        rootElement.style.position = 'relative';
        rootElement.style.width = '100%';
        rootElement.style.height = '100%';

        this._cardHost = document.createElement('div');
        this._cardHost.style.height = '100%';

        rootElement.appendChild(this._cardHost);

        this._designerSurface = document.createElement('div');
        this._designerSurface.classList.add('acd-designersurface');
        this._designerSurface.tabIndex = 0;
        this._designerSurface.style.position = 'absolute';
        this._designerSurface.style.left = '0';
        this._designerSurface.style.top = '0';
        this._designerSurface.style.width = '100%';
        this._designerSurface.style.height = '100%';

        this._designerSurface.addEventListener('keydown', this.handleKeyDown);

        this._designerSurface.addEventListener(
            'contextmenu',
            (ev) => {
                ev.preventDefault();
                if (this.selectedPeer) {
                    this.showCustomMenu(ev);
                }
                return false;
            },
            false,
        );

        rootElement.appendChild(this._designerSurface);

        this.parentElement.innerHTML = '';
        this.parentElement.appendChild(rootElement);
    }

    changeDuplicateId(json: any): void {
        if (json.id) {
            json.id = generateUUID();
        }
        if (json.items) {
            for (let i = 0; i < json.items.length; i++) {
                this.changeDuplicateId(json.items[i]);
            }
        }
        if (json.body) {
            for (let i = 0; i < json.body.length; i++) {
                this.changeDuplicateId(json.body[i]);
            }
        }
        if (json.columns) {
            for (let i = 0; i < json.columns.length; i++) {
                this.changeDuplicateId(json.columns[i]);
            }
        }
        if (json.actions) {
            for (let i = 0; i < json.actions.length; i++) {
                this.changeDuplicateId(json.actions[i]);
            }
        }
        if (json.images) {
            for (let i = 0; i < json.images.length; i++) {
                this.changeDuplicateId(json.images[i]);
            }
        }
        if (json.card) {
            this.changeDuplicateId(json.card);
        }
    }

    addColumnWrapperForPaste(parent: DesignerPeer): DesignerPeer {
        let newColumnElement =
            Adaptive.AdaptiveCard.elementTypeRegistry.createInstance('Column');
        let newColumnPeer =
            CardDesignerSurface.cardElementPeerRegistry.createPeerInstance(
                this,
                parent,
                newColumnElement,
            );
        parent.insertItemAfter(newColumnPeer, this.selectedPeer);
        this.addPeer(newColumnPeer);
        parent.insertChild(newColumnPeer, newColumnPeer.cardElement.index);
        parent.changed(false);
        return newColumnPeer;
    }

    addColumnSetWrapperForPaste(parent: DesignerPeer): DesignerPeer {
        let newColumnSetElement =
            Adaptive.AdaptiveCard.elementTypeRegistry.createInstance(
                'ColumnSet',
            );
        let newColumnSetPeer =
            CardDesignerSurface.cardElementPeerRegistry.createPeerInstance(
                this,
                parent,
                newColumnSetElement,
            );
        parent.insertItemAfter(newColumnSetPeer, this.selectedPeer);
        this.addPeer(newColumnSetPeer);
        parent.insertChild(
            newColumnSetPeer,
            newColumnSetPeer.cardElement.index,
        );
        parent.changed(false);
        return newColumnSetPeer;
    }

    pasteCardElements(parent: DesignerPeer, copiedJSON: any): void {
        let newJSON: any = JSON.parse(JSON.stringify(copiedJSON));
        this.changeDuplicateId(newJSON);
        if (
            parent.getCardObject().getJsonTypeName().startsWith('Action') &&
            !copiedJSON.type.startsWith('Action')
        ) {
            // if trying to paste any other element other than actions or actionSet, then return.
            return;
        }
        if (
            parent.getCardObject().getJsonTypeName() === 'ColumnSet' &&
            newJSON.type !== 'Column'
        ) {
            // pasting elements except column inside a columnSet, needs to be wrapped inside a column
            parent = this.addColumnWrapperForPaste(parent);
        } else if (
            newJSON.type === 'Column' &&
            parent.getCardObject().getJsonTypeName() !== 'ColumnSet'
        ) {
            // pasting a column outside of columnSet, needs to wrap the column by a columnset
            parent = this.addColumnSetWrapperForPaste(parent);
        } else if (newJSON.type === 'ActionSet') {
            if (
                this.selectedPeer
                    .getCardObject()
                    .getJsonTypeName()
                    .startsWith('Action.')
            ) {
                // for pasting actionSet inside an actionSet, we just need to paste individual actions inside the previous actionSet.
                let selectedPeer: DesignerPeer = this.selectedPeer;
                for (let i = 0; i < newJSON.actions.length; i++) {
                    this.pasteActionElements(parent, newJSON.actions[i]);
                    this.setSelectedPeer(selectedPeer);
                }
                return;
            }
        }
        let newCardElement =
            Adaptive.AdaptiveCard.elementTypeRegistry.createInstance(
                copiedJSON.type,
            );
        newCardElement.parse(newJSON, null);
        let newCardElementPeer =
            CardDesignerSurface.cardElementPeerRegistry.createPeerInstance(
                this,
                parent,
                newCardElement,
            );
        parent.insertItemAfter(newCardElementPeer, this.selectedPeer);
        this.addPeer(newCardElementPeer);
        parent.insertChild(
            newCardElementPeer,
            newCardElementPeer.cardElement.index,
        );
        this.render();
    }

    internalPasteActionElements(parent: DesignerPeer, copiedJSON: any): void {
        let newStandAloneActionElement =
            Adaptive.AdaptiveCard.actionTypeRegistry.createInstance(
                copiedJSON.type,
            );
        let newJSON: object = JSON.parse(JSON.stringify(copiedJSON));
        this.changeDuplicateId(newJSON);
        newStandAloneActionElement.parse(newJSON, null);
        parent.cardElement.addAction(newStandAloneActionElement);
        let newStandAloneActionPeer =
            CardDesignerSurface.actionPeerRegistry.createPeerInstance(
                this,
                parent,
                newStandAloneActionElement,
            );
        parent.insertChild(newStandAloneActionPeer);
        this.render();
    }

    pasteActionElements(parent: DesignerPeer, copiedJSON: any): void {
        // pasting standAloneActionPeers
        if (
            !this.selectedPeer
                .getCardObject()
                .getJsonTypeName()
                .startsWith('Action')
        ) {
            // Creating a new actionSet and then adding the button inside the actionSet if the selected peer is not an actionSet.

            if (
                this.selectedPeer.getCardObject().getJsonTypeName() === 'Column'
            ) {
                // pasting actions inside a columnSet we need to wrap it inside a column
                parent = this.addColumnWrapperForPaste(parent);
            }
            let newActionSetElement =
                Adaptive.AdaptiveCard.elementTypeRegistry.createInstance(
                    'ActionSet',
                );
            let newActionSetPeer =
                CardDesignerSurface.cardElementPeerRegistry.createPeerInstance(
                    this,
                    parent,
                    newActionSetElement,
                );
            parent.insertItemAfter(newActionSetPeer, this.selectedPeer);
            this.addPeer(newActionSetPeer);
            parent.insertChild(
                newActionSetPeer,
                newActionSetPeer.cardElement.index,
            );
            parent.changed(false);

            // adding the button to the created actionSet
            this.internalPasteActionElements(newActionSetPeer, copiedJSON);
        } else if (
            this.selectedPeer.getCardObject().getJsonTypeName() === 'ActionSet'
        ) {
            // adding the button to already existing actionSet
            if (this.selectedPeer.getChildCount() + 1 <= MaxActions)
                this.internalPasteActionElements(this.selectedPeer, copiedJSON);
        } else {
            //If the selected item is the button itself while pasting then adding the button to the parent of selected button.
            if (parent.getChildCount() + 1 <= MaxActions)
                this.internalPasteActionElements(parent, copiedJSON);
        }
    }

    onCardValidated: (
        parseErrors: Array<Adaptive.IValidationError>,
        validationResults: Adaptive.ValidationResults,
        syntaxErrors?: string,
    ) => void;
    onSelectedPeerChanged: (peer: DesignerPeer) => void;
    onLayoutUpdated: (isFullRefresh: boolean) => void;

    fixedHeightCard: boolean = false;

    getDesignerSurfaceOffset(): IPoint {
        let clientRect = this._designerSurface.getBoundingClientRect();
        return { x: clientRect.left, y: clientRect.top };
    }

    findDropTarget(pointerPosition: IPoint, peer: DesignerPeer): DesignerPeer {
        return this.internalFindDropTarget(
            pointerPosition,
            this._rootPeer,
            peer,
        );
    }

    findPeer(cardObject: Adaptive.CardObject) {
        for (let peer of this._allPeers) {
            if (peer.getCardObject() === cardObject) {
                return peer;
            }
        }

        return undefined;
    }

    beginUpdate() {
        this._updateCount++;
    }

    endUpdate(renderCard: boolean) {
        if (this._updateCount > 0) {
            this._updateCount--;

            if (this._updateCount === 0) {
                if (renderCard) {
                    this.renderCard();
                }

                this.updateLayout();
            }
        }
    }

    render() {
        this._designerSurface.innerHTML = '';
        this._allPeers = [];

        this.setSelectedPeer(null);

        this.renderCard();

        if (this.card) {
            this._rootPeer =
                CardDesignerSurface.cardElementPeerRegistry.createPeerInstance(
                    this,
                    null,
                    this.card,
                );

            this.addPeer(this._rootPeer);
        }

        this.updateLayout();
    }

    setCardPayloadAsObject(payload: object) {
        this._lastParseErrors = [];

        this.card.parse(payload, this._lastParseErrors);

        this.render();
    }

    setCardPayloadAsString(payload: string) {
        try {
            this.setCardPayloadAsObject(JSON.parse(payload));
        } catch (e) {
            let message: string = e.toString().split('\n')[0];
            this.onCardValidated(
                this._lastParseErrors,
                this.card.validateProperties(),
                message,
            );
        }
    }

    updateLayout(isFullRefresh: boolean = true) {
        for (var i = 0; i < this._allPeers.length; i++) {
            this._allPeers[i].updateLayout();
        }

        if (this.onLayoutUpdated) {
            this.onLayoutUpdated(isFullRefresh);
        }
    }

    removeSelected() {
        if (this.selectedPeer) {
            if (
                this.selectedPeer.getCardObject().getJsonTypeName() ===
                'AdaptiveCard'
            ) {
                let json: object = this.selectedPeer.getCardObject().toJSON();
                json['body'] = [];
                this.selectedPeer.getCardObject().parse(json);
                this.render();
            } else {
                this.beginUpdate();
                try {
                    let parent = this.selectedPeer.parent;

                    if (this.selectedPeer.remove(false, true)) {
                        this.setSelectedPeer(parent);
                        if (this.selectedPeer.getChildCount() === 0) {
                            let json: object = this.selectedPeer
                                .getCardObject()
                                .toJSON();
                            if (json['columns']) json['columns'] = [];
                            if (json['items']) json['items'] = [];
                            if (json['body']) json['body'] = [];
                            if (json['actions']) json['actions'] = [];
                            if (json['images']) json['images'] = [];
                            this.selectedPeer.getCardObject().parse(json);
                        }
                    }
                } finally {
                    this.endUpdate(true);
                }
            }
        }
    }

    handlePointerMove(e: PointerEvent) {
        this._currentMousePosition = { x: e.x, y: e.y };

        if (this.draggedPeer) {
            this._draggedPeerVisual.style.left =
                this._currentMousePosition.x -
                this._draggedPeerDetails.grabPosition.x +
                'px';
            this._draggedPeerVisual.style.top =
                this._currentMousePosition.y -
                this._draggedPeerDetails.grabPosition.y +
                'px';

            const isPointerOverDesigner = this.isPointerOver(
                this._currentMousePosition.x,
                this._currentMousePosition.y,
            );
            if (isPointerOverDesigner) {
                this._draggedPeerVisual.style.cursor = 'grabbing';
                let clientRect = this._designerSurface.getBoundingClientRect();

                this.tryDrop(
                    { x: e.x - clientRect.left, y: e.y - clientRect.top },
                    this.draggedPeer,
                );
            } else {
                this._draggedPeerVisual.style.cursor = 'not-allowed';
            }
        }
    }

    handleEscape(): boolean {
        let handled = false;
        if (this._draggedPeer) {
            this.revertDraggedPeer();
            this.endDrag();
            handled = true;
        } else if (this.selectedPeer) {
            this.setSelectedPeer(null);
            handled = true;
        }
        return handled;
    }

    private revertDraggedPeer() {
        // This is to clear any wrapping happened while dragging the peer for example, action might be wrapped with actionset
        this._draggedPeerDetails.peer.remove(false, false);
        if (
            (this._draggedPeerDetails.peer instanceof CardElementPeer &&
                this._draggedPeerDetails.peer.cardElement.parent) ||
            (this._draggedPeerDetails.peer instanceof ActionPeer &&
                this._draggedPeerDetails.peer.action.parent)
        ) {
            if (this._draggedPeerDetails.peer.parent) {
                this._draggedPeerDetails.peer.parent.removeChild(
                    this._draggedPeerDetails.peer,
                );
            }
        }

        this._draggedPeer.remove(false, false);
        if (
            (this._draggedPeer instanceof CardElementPeer &&
                this._draggedPeer.cardElement.parent) ||
            (this._draggedPeer instanceof ActionPeer &&
                this._draggedPeer.action.parent)
        ) {
            if (this._draggedPeer.parent) {
                this._draggedPeer.parent.removeChild(this._draggedPeer);
            }
        }

        if (this._draggedPeerDetails.isNewPeer) {
            this._draggedPeerDetails.parent.removeChild(this._draggedPeer);
        } else {
            const siblingsCount =
                this._draggedPeerDetails.parent.getChildCount();
            if (siblingsCount <= this._draggedPeerDetails.index) {
                this._draggedPeerDetails.parent.insertItemBefore(
                    this._draggedPeerDetails.peer,
                    null,
                );
            } else {
                this._draggedPeerDetails.parent.insertItemBefore(
                    this._draggedPeerDetails.peer,
                    this._draggedPeerDetails.parent.getChildAt(
                        this._draggedPeerDetails.index,
                    ),
                );
            }
            this._draggedPeerDetails.parent.insertChild(
                this._draggedPeerDetails.peer,
                this._draggedPeerDetails.index,
            );
        }
        this._draggedPeerDetails.parent.changed(false);
        this.updateLayout();
    }

    handlePointerUp() {
        if (!this._draggedPeer) {
            return;
        }

        const isPointerOver = this.isPointerOver(
            this._currentMousePosition.x,
            this._currentMousePosition.y,
        );
        if (!isPointerOver) {
            this.revertDraggedPeer();
        }
        this.endDrag();
    }

    startDrag(
        peer: DesignerPeer,
        e: PointerEvent = null,
        isNewPeer: boolean = false,
    ) {
        if (!this.draggedPeer) {
            this._preventJsonUpdate = true;

            this._designerSurface.classList.add('dragging');

            this.draggedPeer = peer;

            this._draggedPeerVisual = peer.renderDragVisual();
            this._draggedPeerVisual.classList.add('draggedPeerVisual');
            this._draggedPeerVisual.style.position = 'absolute';
            this._draggedPeerVisual.style.left =
                this._currentMousePosition.x + 'px';
            this._draggedPeerVisual.style.top =
                this._currentMousePosition.y + 'px';
            this._draggedPeerVisual.style.zIndex = '1000';
            this._draggedPeerVisual.style.backgroundColor = '#fff';
            this._draggedPeerVisual.style.boxShadow =
                '0 4.8px 14.4px 0 rgba(0, 0, 0, 0.18)';
            this._draggedPeerVisual.style.cursor = 'grabbing';
            document.body.appendChild(this._draggedPeerVisual);

            let grabPosition: IPoint;
            if (e) {
                const peerBox =
                    this.draggedPeer.renderedElement.getBoundingClientRect();
                grabPosition = {
                    x: e.x - peerBox.left,
                    y: e.y - peerBox.top,
                };
            } else {
                const visualBox =
                    this._draggedPeerVisual.getBoundingClientRect();
                grabPosition = {
                    x: visualBox.width / 2,
                    y: visualBox.height / 2,
                };
            }
            this._draggedPeerDetails = {
                peer: this.draggedPeer,
                parent: this.draggedPeer.parent,
                index: this.draggedPeer.parent.getChildIndex(this.draggedPeer),
                isNewPeer,
                grabPosition,
            };

            this.setSelectedPeer(this.draggedPeer);
        }
    }

    endDrag() {
        if (this.draggedPeer) {
            this._preventJsonUpdate = false;

            if (this.onLayoutUpdated) {
                this.onLayoutUpdated(true);
            }

            // Ensure that the dragged peer's elements are at the top in Z order
            this.draggedPeer.removeElementsFromDesignerSurface(true);
            this.draggedPeer.addElementsToDesignerSurface(
                this._designerSurface,
                true,
            );

            this._dropTarget.renderedElement.classList.remove('dragover');

            this._peerToInsert = null;

            this.draggedPeer = null;

            this._draggedPeerVisual.remove();

            this._draggedPeerVisual = null;

            this._designerSurface.classList.remove('dragging');
            this.render();
            if (this._draggedPeerDetails.parent.getChildCount() === 0) {
                let json: object = this._draggedPeerDetails.parent
                    .getCardObject()
                    .toJSON();
                if (json['columns']) json['columns'] = [];
                if (json['items']) json['items'] = [];
                if (json['body']) json['body'] = [];
                if (json['actions']) json['actions'] = [];
                if (json['images']) json['images'] = [];
                this._draggedPeerDetails.parent.getCardObject().parse(json);
            }
        }
    }

    modifyDraggedPeer(peer: DesignerPeer) {
        if (this.draggedPeer) {
            this.draggedPeer = peer;
            this.setSelectedPeer(this.draggedPeer);
        }
    }

    tryDrop(pointerPosition: IPoint, peer: DesignerPeer): boolean {
        let result = false;

        if (peer) {
            if (this._dropTarget) {
                this._dropTarget.renderedElement.classList.remove('dragover');
            }

            const newDropTarget = this.findDropTarget(pointerPosition, peer);

            if (newDropTarget) {
                this._dropTarget = newDropTarget;
                this._dropTarget.renderedElement.classList.add('dragover');

                result = this._dropTarget.tryDrop(peer, pointerPosition);
            }
        }

        return result;
    }

    isPointerOver(x: number, y: number) {
        let clientRect = this._designerSurface.getBoundingClientRect();

        return (
            x >= clientRect.left &&
            x <= clientRect.right &&
            y >= clientRect.top &&
            y <= clientRect.bottom
        );
    }

    pageToClientCoordinates(x: number, y: number): IPoint {
        let clientRect = this._designerSurface.getBoundingClientRect();

        return {
            x: x - clientRect.left,
            y: y - clientRect.top,
        };
    }

    clearSelectedPeer() {
        this.setSelectedPeer(null);
    }

    get rootPeer(): DesignerPeer {
        return this._rootPeer;
    }

    get selectedPeer(): DesignerPeer {
        return this._selectedPeer;
    }

    get card(): Adaptive.AdaptiveCard {
        return this._card;
    }

    set card(value: Adaptive.AdaptiveCard) {
        if (value !== this._card) {
            if (this._card) {
                this._card.onInlineCardExpanded = null;
            }

            this._card = value;

            if (this._card) {
                this._card.onInlineCardExpanded = (
                    action: Adaptive.ShowCardAction,
                    isExpanded: boolean,
                ) => {
                    this.inlineCardExpanded(action, isExpanded);
                };
            }

            this.render();
        }
    }

    get designerSurfaceElement() {
        return this._designerSurface;
    }

    get preventJsonUpdate() {
        return this._preventJsonUpdate;
    }

    get peerCount() {
        return this._allPeers.length;
    }

    get peerToInsert() {
        return this._peerToInsert;
    }

    set peerToInsert(peer: DesignerPeer) {
        this._peerToInsert = peer;
    }
}
